import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { GroupVisibility, UserDto } from '../../api/types';
import { canCreateEvent } from '../../domain/events/policy';
import { GROUP_DESCRIPTION_MIN_LENGTH, validateGroupFormDraft, encodeEmblemInDescription, parseEmblemFromDescription } from '../../domain/groups/groupForm';
import { canEditGroup } from '../../domain/groups/policy';
import { InvitedUsersList } from '../../features/selection/InvitedUsersList';
import { MemberSelectModal } from '../../features/selection/MemberSelectModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createGroup, inviteToGroup, updateGroup } from '../../store/thunks';

import './GroupFormPage.css';

type FieldProps = {
  label: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
};

function Field({ label, required, error, children }: FieldProps) {
  return (
    <div className="ttbField">
      <div className={`ttbFieldLabel${required ? ' ttbFieldLabel-required' : ''}`}>
        {label}
      </div>
      {children}
      {error ? <div className="ttbFieldError">{error}</div> : null}
    </div>
  );
}

export function GroupFormPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();

  const role = useAppSelector((s) => s.auth.user?.role);
  const currentUserTelegramId = useAppSelector((s) => s.auth.user?.telegramId);
  const canCreate = canCreateEvent(role);
  const allUsers = useAppSelector((s) => s.entities.users.items);
  const groups = useAppSelector((s) => s.entities.groups.items);

  const isEditMode = !!groupId;
  const currentGroup = isEditMode ? groups.find((g) => g.id === groupId) : null;

  const [name, setName] = React.useState('');
  const [emblem, setEmblem] = React.useState('🛡️');
  const [description, setDescription] = React.useState('');
  const [visibility, setVisibility] = React.useState<GroupVisibility>('private');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [memberModalOpen, setMemberModalOpen] = React.useState(false);
  const [invitedUserIds, setInvitedUserIds] = React.useState<string[]>([]);

  const [touched, setTouched] = React.useState({ 
    name: false, 
    emblem: false,
    description: false 
  });

  React.useEffect(() => {
    if (!role) return;
    if (isEditMode) {
      const canEdit = canEditGroup({ role, group: currentGroup, currentUserTelegramId });
      if (!canEdit) navigate(-1);
    } else {
      if (!canCreate) navigate(-1);
    }
  }, [canCreate, navigate, role, isEditMode, currentGroup, currentUserTelegramId]);

  React.useEffect(() => {
    if (isEditMode && currentGroup) {
      setName(currentGroup.name);
      const parsed = parseEmblemFromDescription(currentGroup.description);
      setEmblem(parsed.emblem);
      setDescription(parsed.description);
      setVisibility(currentGroup.visibility);
      setInvitedUserIds(currentGroup.members.map(m => m.telegramId));
    }
  }, [isEditMode, currentGroup]);

  const draft = React.useMemo(() => ({ name, emblem, description }), [name, emblem, description]);
  const validation = React.useMemo(() => validateGroupFormDraft(draft), [draft]);

  const nameError = touched.name ? validation.errors.name : null;
  const emblemError = touched.emblem ? validation.errors.emblem : null;
  const descriptionError = touched.description ? validation.errors.description : null;

  const isValid = validation.isValid;

  const invitedUsers = React.useMemo<UserDto[]>(() => {
    return allUsers.filter((u) => invitedUserIds.includes(u.telegramId));
  }, [allUsers, invitedUserIds]);

  const handleAddMember = (telegramId: string) => {
    if (!invitedUserIds.includes(telegramId)) {
      setInvitedUserIds([...invitedUserIds, telegramId]);
    }
  };

  const handleRemoveUser = (telegramId: string) => {
    setInvitedUserIds(invitedUserIds.filter((id) => id !== telegramId));
  };

  const onSubmit = async () => {
    setTouched({ name: true, emblem: true, description: true });
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const encodedDescription = encodeEmblemInDescription(emblem, validation.normalizedDescription);

      if (isEditMode && groupId) {
        await dispatch(
          updateGroup({
            groupId,
            payload: {
              name: validation.normalizedName,
              description: encodedDescription,
              visibility,
            },
          }),
        ).unwrap();
      } else {
        await dispatch(
          createGroup({
            name: validation.normalizedName,
            description: encodedDescription,
            visibility,
            participants: invitedUserIds.map(id => ({ telegramId: id })),
          }),
        ).unwrap();
      }

      navigate(-1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ttbPage">
      <div className="ttbFormContainer">
        <div className="ttbFormHeader">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            ← Назад
          </button>
          <div className="ttbFormHeaderTitle">{isEditMode ? 'Редактировать отряд' : 'Создать отряд'}</div>
        </div>

        {/* Основная информация */}
        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">📋</span>
            <h2 className="ttbFormSectionTitle">Основная информация</h2>
          </div>

          <Field label="Название отряда" required error={nameError}>
            <input
              type="text"
              placeholder="Введите название"
              value={name}
              maxLength={50}
              disabled={isSubmitting}
              onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              onChange={(e) => setName(e.target.value)}
              className={['ttbInput', nameError ? 'ttbInputError' : null].filter(Boolean).join(' ')}
            />
            <div className="ttbFieldHint">Уникальное название (макс. 50 символов)</div>
          </Field>

          <Field label="Эмблема отряда" required error={emblemError}>
            <div className="emblem-picker">
              {['🛡️', '⚔️', '🏹', '🔮', '⚡', '🌟', '🐉', '🦅', '🐺', '👑'].map((emoji) => (
                <div key={emoji} className="emblem-option">
                  <input
                    type="radio"
                    name="emblem"
                    id={`emblem-${emoji}`}
                    value={emoji}
                    checked={emblem === emoji}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      setEmblem(e.target.value);
                      setTouched((t) => ({ ...t, emblem: true }));
                    }}
                  />
                  <label htmlFor={`emblem-${emoji}`} className="emblem-icon">{emoji}</label>
                </div>
              ))}
            </div>
          </Field>

          <Field label="Описание" required error={descriptionError}>
            <textarea
              placeholder="Расскажите о вашем отряде..."
              value={description}
              maxLength={300}
              disabled={isSubmitting}
              onBlur={() => setTouched((t) => ({ ...t, description: true }))}
              onChange={(e) => setDescription(e.target.value)}
              className={['ttbInput', 'ttbInputTextArea', descriptionError ? 'ttbInputError' : null]
                .filter(Boolean)
                .join(' ')}
            />
            <div
              className="char-counter"
              style={{
                color: description.length < GROUP_DESCRIPTION_MIN_LENGTH ? 'var(--color-danger-500)' : 'var(--color-text-secondary)',
              }}
            >
              {description.length} / 300
            </div>
            <div className="ttbFieldHint">Минимум {GROUP_DESCRIPTION_MIN_LENGTH} символов, расскажите о вашем отряде</div>
          </Field>
        </div>

        {/* Тип видимости */}
        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">🔓</span>
            <h2 className="ttbFormSectionTitle">Тип видимости</h2>
          </div>

          <Field label="Кто может вступить в отряд?">
            <div className="visibility-options">
              <label className={`visibility-option${visibility === 'open' ? ' visibility-option-selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="open"
                  checked={visibility === 'open'}
                  disabled={isSubmitting}
                  onChange={(e) => setVisibility(e.target.value as GroupVisibility)}
                />
                <div className="visibility-option-content">
                  <div className="visibility-option-title">🌍 Открытая группа</div>
                  <div className="visibility-option-desc">Любой участник клуба может вступить самостоятельно</div>
                </div>
              </label>

              <label className={`visibility-option${visibility === 'private' ? ' visibility-option-selected' : ''}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === 'private'}
                  disabled={isSubmitting}
                  onChange={(e) => setVisibility(e.target.value as GroupVisibility)}
                />
                <div className="visibility-option-content">
                  <div className="visibility-option-title">🔒 Приватная группа</div>
                  <div className="visibility-option-desc">Только создатель может приглашать участников</div>
                </div>
              </label>
            </div>
          </Field>
        </div>

        {/* Пригласить участников */}
        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">👥</span>
            <h2 className="ttbFormSectionTitle">Пригласить участников</h2>
          </div>

          <div className="ttbFieldHint" style={{ marginBottom: 'var(--space-3)' }}>
            Вы можете пригласить участников в отряд заранее
          </div>

          <button
            type="button"
            onClick={() => setMemberModalOpen(true)}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
            style={{ marginBottom: 'var(--space-3)', width: '100%' }}
          >
            ➕ Пригласить участника
          </button>

          <InvitedUsersList users={invitedUsers} onRemove={handleRemoveUser} />
        </div>

        <div className="ttbFormActions">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!isValid || isSubmitting}
            className="ttbButton ttbButtonSubmit"
          >
            {isSubmitting ? 'Создание…' : '✨ Создать отряд'}
          </button>
        </div>
      </div>

      {/* Modal */}
      <MemberSelectModal
        isOpen={memberModalOpen}
        onClose={() => setMemberModalOpen(false)}
        onSelect={handleAddMember}
        excludeIds={[...invitedUserIds, ...(currentUserTelegramId ? [currentUserTelegramId] : [])]}
      />
    </div>
  );
}
